import type {MithraMaterializedApi} from "../../../services/MithraMaterializedApi";
import {makeAutoObservable, reaction} from "mobx";
import {selectMatReviewLevelStatistics} from "../../../services/classes/MatReviewHelpers";
import type {single_bar_chart} from "../../../components/visualization/single-barchart/SingleBarChartBuilder";
import {convertSingleToChart} from "../../../services/classes/CategorizationHelpers";
import type {CurrencyAbbreviation} from "../../../components/currency-component/CurrencyClasses";
import type AuthStore from "../../../authentication/AuthStore";
import ProfileStore from "../../../stores/ProfileStore";
import {CategorizationReviewPageFilterController} from "./CategorizationReviewPageFilterController.ts";
import {CategorizationReviewPageStatisticsController} from "./CategorizationReviewPageStatisticsController";
import {CategorizationReviewPageApprovalController} from "./CategorizationReviewPageApprovalController";
import type {Page} from "./CategorizationReviewPageStateController";
import {CategorizationReviewPageStateController} from "./CategorizationReviewPageStateController";
import type {ApprovalStore} from "../../../stores/ApprovalStore";
import type {BagStore} from "../../../stores/BagStore.tsx";
import type {CategorizationReviewStore} from "../../../stores/categorization-review/CategorizationReviewStore";
import {CategorizationReviewPageRevisionController} from "./CategorizationReviewPageRevisionController";
import {CategorizationReviewPageMuixSelectionController} from "./CategorizationReviewPageMuixSelectionController";
import type * as CategorizationReviewUrls from "../classes/CategorizationReviewUrls";
import CategorizationReviewPageTableController from "./CategorizationReviewPageTableController.ts";
import {GridGetRowsError, GridUpdateRowError} from "@mui/x-data-grid-premium";
import type {AxiosError} from "axios";
import {isAxiosError} from "axios";
import {CategorizationReviewApi} from "./CategorizationReviewApi.ts";
import {axiosErrorToString} from "../../../utils/axios.utils.ts";
import {SECONDS, UNCATEGORIZED_LABEL} from "../../../constants.ts";
import {delay, of, Subject, switchMap} from "rxjs";
import {
    CATEGORIZATION_DEFAULT_GROUP_BY
} from "../components/review-table/review-table-muix/CategorizationTableConstants.ts";
import {keyOfMatPartReviewRowResp} from "../../../services/classes/MaterializedClasses.ts";
import {environment} from "../../../env.ts";

export type SummaryKeyValues = {
    abbreviation: CurrencyAbbreviation,
    after: {
        classified_spend: number, // total - unclassified
        reclassified_spend: number,
        unclassified_spend: number,
    }
}

const SLOW_LOADING_THRESHOLD = 10 * SECONDS;

/**
 * Class to manage the data and interactions of the categorization review page
 *
 * This class contains a separate controller for each logical part of the page.
 * Additionally, it contains some higher level logic that connects the different parts.
 */
export class CategorizationReviewPageController {

    readonly reviewPageTableController: CategorizationReviewPageTableController;
    readonly reviewPageStateController: CategorizationReviewPageStateController;
    readonly reviewPageFilterController: CategorizationReviewPageFilterController;
    readonly reviewPageSelectionController: CategorizationReviewPageMuixSelectionController;
    readonly reviewPageStatisticsController: CategorizationReviewPageStatisticsController;
    readonly reviewPageApprovalController: CategorizationReviewPageApprovalController
    readonly reviewPageRevisionController: CategorizationReviewPageRevisionController;
    readonly reviewApi: CategorizationReviewApi;

    /**
     * Generic loading state
     * TODO: Add pipe loading as well
     */
    isLoadingDataSlow = false;
    readonly loadingSubject = new Subject<boolean>();

    /**
     * Error for review page
     */
    errorMessage: string = '';

    public isGroupBy: boolean = CATEGORIZATION_DEFAULT_GROUP_BY;

    constructor(
        public categorizationStore: CategorizationReviewStore,
        private approvalStore: ApprovalStore,
        private api: MithraMaterializedApi,
        private auth: AuthStore,
        private profile: ProfileStore,
        private bagStore: BagStore,
    ) {
        makeAutoObservable(this, {loadingSubject: false})
        this.reviewPageTableController = new CategorizationReviewPageTableController(this, this.api, this.auth, this.bagStore);
        this.reviewPageStateController = new CategorizationReviewPageStateController(this, this.profile, this.auth);
        this.reviewPageFilterController = new CategorizationReviewPageFilterController(this, this.profile, this.bagStore.bu);
        this.reviewPageSelectionController = new CategorizationReviewPageMuixSelectionController(this, this.api);
        this.reviewPageStatisticsController = new CategorizationReviewPageStatisticsController(this.profile, this.api);
        this.reviewPageApprovalController = new CategorizationReviewPageApprovalController(this, this.approvalStore, this.api)
        this.reviewPageRevisionController = new CategorizationReviewPageRevisionController(this, this.api, this.auth, this.profile);
        this.reviewApi = new CategorizationReviewApi(this, this.api);

        // Monitor loading state to show slow loading message
        this.loadingSubject.pipe(
            switchMap(loading => loading
                ? of(true).pipe(delay(SLOW_LOADING_THRESHOLD))
                : of(false))
        ).subscribe(isSlowLoading => this.setIsSlowLoading(isSlowLoading));
        reaction(() => this.isLoadingData, isLoadingData => this.loadingSubject.next(isLoadingData));
        reaction(() => this.reviewPageFilterController.selectedBusinessUnitId, bu_id =>
            this.reviewPageStatisticsController.request(
                this.bagId,
                bu_id,
                this.categorizationStore.taxonomySize))
    }

    get bagId(): number {
        return this.categorizationStore.bagId;
    }

    get revisionId(): number | undefined {
        return typeof this.reviewPageRevisionController.revision === 'number'
            ? this.reviewPageRevisionController.revision : undefined
    }

    get isMergedBag(): boolean {
        return Boolean(this.profile.p.hardcodedCombinedBagId && this.bagId === this.profile.p.hardcodedCombinedBagId)
    }

    get taxonomySize(): number {
        return this.categorizationStore.taxonomySize;
    }

    get isValidationBag(): boolean {
        return this.categorizationStore.isValidationBag;
    }

    get _selectedStats() {
        const s = this.reviewPageStatisticsController.reviewLevelStatistics;
        if (!s) {
            return undefined
        }
        return selectMatReviewLevelStatistics(s, this.reviewPageFilterController.selectedCategory)
    }

    get parentCharts(): {
        charts: {
            category: string,
            data: single_bar_chart.Data
        }[],
        max: number,
    } | undefined {
        if (!this._selectedStats) return undefined;
        const parentStats = this._selectedStats.slice(1);
        if (parentStats.length === 0) return undefined;
        const charts = parentStats.map((stat, i) => {
            const aiChangeChart = convertSingleToChart(stat, this.profile.currencySymbol, true);
            const data: single_bar_chart.Data = {
                mainLabel: `L${i + 1}: ${aiChangeChart.label}`,
                values: aiChangeChart.values,
            }
            return {category: aiChangeChart.category, data};
        })
        const COL_WIDTH = 6 / 9 // Show as width col=6, while the whole viz goes to col=9
        const max = Math.max(...charts.map(c => Math.max(...c.data.values.map(v => v.value)))) / COL_WIDTH;
        return {charts, max}
    }

    get reviewPageIsLoading() {
        return this.reviewPageStatisticsController._reviewStatistics.busy
            || this.reviewPageStatisticsController._reviewLevelStatistics.busy
    }

    navigateToPage(page: Page) {
        if (page === 'welcome' || page === 'review' || page === 'review-dashboard') {
            this.reviewPageFilterController.clearFilters();
        }
        this.reviewPageStateController.setPage(page);
    }

    init() {
        this.loadData(true);
    }

    loadData(initialLoad = false, resetPagination = false) {
        const bagId = this.bagId;
        const taxonomySize = this.categorizationStore.taxonomySize;
        const businessUnitId = this.reviewPageFilterController.selectedBusinessUnitId;
        console.debug('CategorizationReviewPageController.loadData: ', {
            initialLoad,
            bagId,
            resetPagination
        });
        this.reviewPageSelectionController.init();
        if (bagId === -1) {
            // Reset everything
            this.reviewPageStatisticsController.reset();
        } else if (bagId) {
            if (initialLoad) {
                this.reviewPageStatisticsController.request(bagId, businessUnitId, taxonomySize)
            }
        }
    }

    setIsGroupBy(value: boolean) {
        this.isGroupBy = value;
        this.reviewPageTableController.gridApi?.dataSource?.cache?.clear();
        this.reviewPageTableController.setInvalidSortKeyWarnings();
        this.reviewPageSelectionController.clearSelection();
    }

    public initializeFromIntent(intent: CategorizationReviewUrls.Intent) {
        if (intent.type === 'by_supplier') {
            const supplierParam = intent.supplier;
            this.navigateToPage('review');
            this.reviewPageFilterController.setSupplierSearch(supplierParam);
            // this.reviewPageFilterController.suppliersSearchManager.search.setSearch(supplierParam);
            // this.reviewPageFilterController.suppliersSearchManager.search.doSearch('url');
        } else if (intent.type === 'by_part') {
            const searchValueParam = intent.part_search;
            const searchTypeParam = intent.part_search_fields;
            this.navigateToPage('review');
            this.reviewPageFilterController.setSearch(searchTypeParam, searchValueParam);
            // this.reviewPageFilterController.partsSearchManager.setSelectedKeys(searchTypeParam);
            // this.reviewPageFilterController.partsSearchManager.search.setSearch(searchValueParam);
            // this.reviewPageFilterController.partsSearchManager.search.doSearch('url');
        }
    }

    get isLoadingData(): boolean {
        return this.reviewApi.nInPipe > 0;
    }

    setIsSlowLoading(isLoadingDataSlow: boolean) {
        this.isLoadingDataSlow = isLoadingDataSlow;
    }

    onNoError() {
        this.errorMessage = ''
    }

    onError(error: GridUpdateRowError | GridGetRowsError | AxiosError | Error | undefined) {
        this.errorMessage = this.getErrorMessage(error);
    }

    private getErrorMessage(error: GridUpdateRowError | GridGetRowsError | AxiosError | Error | undefined, fallbackMessage?: string): string {
        if (!error) {
            return fallbackMessage ?? 'Unknown error'
        }
        if (isAxiosError(error)) {
            if (this.auth.isMithraStaff && error.response?.data) {
                const errorItems = error.response.data?.filterModel?.items;
                if (Array.isArray(errorItems)) {
                    return 'FilterModel.items: ' + errorItems.filter(i => i).map(item => `${item.message ?? JSON.stringify(item)}`).join(', ')
                }
                return axiosErrorToString(error)
            }
            return error.message
        }
        if (error instanceof GridGetRowsError) {
            return this.getErrorMessage(error.cause, error.message)
        }
        if (error instanceof GridUpdateRowError) {
            return this.getErrorMessage(error.cause, error.message)
        }
        return fallbackMessage ?? String(error)
    }

    /**
     * Note: Maybe move this one somewhere else? depends on usage after delivery...
     */
    get relatedDataFieldsSpec(): Record<string, string[]> {
        const result: Record<string, string[]> = {
            // Note that s_name is included in MatPartReviewRow
            supplier: [],
            // Ensure we can filter on bu_name
            business_unit: ['bu_name',],
            // Always add cat as related to enable category search
            cat: Array.from(Array(8).keys()).map(i => 'l' + String(i + 1)),
            // Add base part relation
            part: [],
        };
        if (this.auth.isMithraStaffGroup || environment.isTestReviewPage)
            result['part'].push('p_id');
        if (this.isMergedBag)
            result['part'].push('src_databag_id');
        if (this.bagStore.configStore.getConfigKey('CATEGORIZATION_FILE_DL') ||
            this.profile.isCategorizationShowFileDownloadHack(this.bagId)) {
            result['part'].push('file_name');
            // in case that file download is enabled, p_id is necessary
            if (!result['part'].includes('p_id')) result['part'].push('p_id');
        }
        if (this.bagStore.categorizationColumns) {
            this.bagStore.categorizationColumns.filter(col =>
                col.in_use && col.table_name === 'data_source_supplier' &&
                !keyOfMatPartReviewRowResp.includes(col.column_name) &&
                !result['supplier'].includes(col.column_name)
            ).forEach(col => result['supplier'].push(col.column_name));
            this.bagStore.categorizationColumns.filter(col =>
                col.in_use && col.table_name === 'data_source_part' &&
                !keyOfMatPartReviewRowResp.includes(col.column_name) &&
                !result['part'].includes(col.column_name)
            ).forEach(col => result['part'].push(col.column_name));
        }
        return result;
    }

    // noinspection JSUnusedLocalSymbols
    public exceljsPostProcess: ({workbook, worksheet}) => Promise<void> = async (
        {workbook, worksheet}) => {
        const catColumn = worksheet.getRow(1)._cells.find(c => c.value === 'Category');
        const previousCatColumn = worksheet.getRow(1)._cells.find(c => c.value === 'Previous category');
        if (catColumn)
            worksheet.columns = worksheet.columns.concat(Array(this.bagStore.taxonomy_size).fill(0).map((_, i) => {
                return {header: `L${i + 1}`, key: `L${i + 1}`}
            }));
        if (previousCatColumn)
            worksheet.columns = worksheet.columns.concat(Array(this.bagStore.taxonomy_size).fill(0).map((_, i) => {
                return {header: `Previous L${i + 1}`, key: `Previous L${i + 1}`}
            }));
        worksheet.getRows(2, worksheet.rowCount - 1).forEach((row) => {
            let values = row.values;
            const catValues = (catColumn && values[catColumn.col]) ?
                values[catColumn.col].split(' > ').slice(0, this.bagStore.taxonomy_size) : [];
            const previousCatValues = (previousCatColumn && values[previousCatColumn.col]) ?
                values[previousCatColumn.col].split(' > ').slice(0, this.bagStore.taxonomy_size) : [];
            Array(this.bagStore.taxonomy_size).fill(0).forEach((_, i) => {
                if (catColumn && !catValues[i]) catValues[i] = UNCATEGORIZED_LABEL;
                if (previousCatColumn && !previousCatValues[i]) previousCatValues[i] = UNCATEGORIZED_LABEL;
            });
            if (catColumn)
                values = values.concat(catValues);
            if (previousCatColumn)
                values = values.concat(previousCatValues);
            row.values = values;
        });
        if (catColumn)
            worksheet.getColumn(catColumn.col).hidden = true;
        if (previousCatColumn)
            worksheet.getColumn(previousCatColumn.col).hidden = true;
    }
}
